import { ReactNode } from 'react'
import { Badge } from './badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover'
import { Building2 } from 'lucide-react'

export interface BadgeListItem {
  id: string | number
  name: string
  shortName?: string
  badgeClassName?: string
  [key: string]: any // Permite más propiedades en caso de ser necesario
}

export interface BadgeListPopoverProps {
  items: BadgeListItem[]
  globalAccess?: boolean
  globalLabel?: string
  emptyLabel?: string
  popoverTitle?: string
  icon?: ReactNode
}

export function BadgeListPopover({
  items,
  globalAccess = false,
  globalLabel = 'Acceso total',
  emptyLabel = 'Sin asignar',
  popoverTitle = 'Elementos Asignados',
  icon = <Building2 className="h-4 w-4 text-card-description shrink-0" />
}: BadgeListPopoverProps) {
  
  if (globalAccess) {
    return (
      <Badge variant="secondary" className="bg-card hover:bg-table-row-hover text-card-foreground border border-border font-medium">
        {globalLabel}
      </Badge>
    )
  }
  
  if (!items || items.length === 0) {
    return <span className="text-sm text-card-description italic">{emptyLabel}</span>
  }

  const firstItem = items[0]
  const remaining = items.slice(1)

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
      <Badge variant="secondary" className={`font-medium whitespace-normal break-words text-wrap max-w-full text-left h-auto py-1.5 px-2.5 ${firstItem.badgeClassName || 'bg-card hover:bg-table-row-hover text-card-foreground border border-border'}`}>
        <span className="block">{firstItem.shortName || firstItem.name}</span>
      </Badge>

      {remaining.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center justify-center rounded-md border border-border bg-card px-1.5 py-0.5 text-xs font-medium text-card-foreground transition-colors hover:bg-table-row-hover focus:outline-none focus:ring-2 focus:ring-primary/40">
              +{remaining.length}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-max max-w-[320px] rounded-lg border border-border bg-card p-0 text-card-foreground shadow-md" side="top" align="start">
            <div className="border-b border-border bg-table-header-bg px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-table-header-foreground">
                {popoverTitle} ({items.length})
              </p>
            </div>
            <div className="max-h-[220px] overflow-y-auto p-1 space-y-0.5 custom-scrollbar">
              {items.map(item => (
                <div key={item.id} className="flex cursor-default items-center gap-2.5 rounded-md px-2.5 py-1.5 transition-colors hover:bg-table-row-hover">
                  {icon}
                  <span className="text-sm font-medium text-card-foreground">{item.name}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
